import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { Question, QuizState, CategoryCounts } from '../../types';
import { saveQuizState, loadQuizState } from '../../utils/localStorage';

// Try to load saved state from localStorage, otherwise use default initial state
const savedState = loadQuizState();

const defaultInitialState: QuizState = {
  questions: [],
  status: 'loading', // 'loading', 'error', 'ready', 'active', 'email-collection', 'finished'
  index: 0,
  answer: null,
  categoryCounts: { A: 0, B: 0, C: 0, D: 0 },
  answers: new Array(10).fill(-1),
  resultsSubmitted: false
};

// Ensure saved state has all required properties
const initialState: QuizState = savedState ? {
  ...defaultInitialState,
  ...savedState,
  answers: savedState.answers || [],
  resultsSubmitted: savedState.resultsSubmitted || false
} : defaultInitialState;

const quizSlice = createSlice({
  name: 'quiz',
  initialState,
  reducers: {
    dataReceived(state, action: PayloadAction<Question[]>) {
      state.questions = action.payload;

      if (!state.answers || state.answers.length === 0) {
        state.answers = new Array(10).fill(-1);
      }

      // If we have saved state with active, email-collection, or finished status, keep it
      // Otherwise, set to ready
      if (!savedState || (savedState.status !== 'active' && savedState.status !== 'email-collection' && savedState.status !== 'finished')) {
        state.status = 'ready';
      }

      // Save state to localStorage
      saveQuizState({
        ...state,
        questions: action.payload
      });
    },
    dataFailed: (state) => {
      state.status = 'error';
      saveQuizState(state);
    },
    startQuiz: (state) => {
      state.status = 'active';
      saveQuizState(state);
    },
    newAnswer(state, action: PayloadAction<number>) {
      const question = state.questions[state.index];
      const newAnswerIndex = action.payload;

      // If there was a previous answer, decrement its category count
      if (state.answer !== null) {
        const previousOption = question.options[state.answer];
        const previousCategory = previousOption.category;
        state.categoryCounts[previousCategory as keyof CategoryCounts]--;
      }

      // Set the new answer and increment its category count
      state.answer = newAnswerIndex;
      const selectedOption = question.options[newAnswerIndex];
      const category = selectedOption.category;
      state.categoryCounts[category as keyof CategoryCounts]++;

      // Store the answer in the answers array
      if (state.answers && state.answers.length > state.index) {
        state.answers[state.index] = newAnswerIndex;
      }

      // Save state to localStorage
      saveQuizState(state);
    },
    nextQuestion: (state) => {
      // Store the current answer before moving to next question
      if (state.answer !== null && state.answers && state.answers.length > state.index) {
        state.answers[state.index] = state.answer;
      }
      state.index += 1;
      state.answer = null;
      saveQuizState(state);
    },
    finishQuiz: (state) => {
      // Store the final answer
      if (state.answer !== null && state.answers && state.answers.length > state.index) {
        state.answers[state.index] = state.answer;
      }
      state.status = 'email-collection';
      saveQuizState(state);
    },
    collectEmail: (state) => {
      state.status = 'finished';
      saveQuizState(state);
    },
    restartQuiz: (state) => {
      const newState = {
        ...defaultInitialState,
        questions: state.questions,
        status: 'ready' as const,
        resultsSubmitted: false,
      };
      saveQuizState(newState);
      return newState;
    },
    markResultsSubmitted: (state) => {
      state.resultsSubmitted = true;
      saveQuizState(state);
    },

  },
});

export const {
  dataReceived,
  dataFailed,
  startQuiz,
  newAnswer,
  nextQuestion,
  finishQuiz,
  collectEmail,
  restartQuiz,
  markResultsSubmitted
} = quizSlice.actions;

export default quizSlice.reducer;

// Selectors
export const selectQuestions = (state: RootState): Question[] => state.quiz.questions;
export const selectStatus = (state: RootState): QuizState['status'] => state.quiz.status;
export const selectIndex = (state: RootState): number => state.quiz.index;
export const selectAnswer = (state: RootState): number | null => state.quiz.answer;
export const selectCategoryCounts = (state: RootState): CategoryCounts => state.quiz.categoryCounts;
export const selectAnswers = (state: RootState): number[] => state.quiz.answers;
export const selectActiveQuestion = (state: RootState): Question | undefined => state.quiz.questions[state.quiz.index];
export const selectNumQuestions = (state: RootState): number => state.quiz.questions.length;
export const selectResultsSubmitted = (state: RootState): boolean => state.quiz.resultsSubmitted;
